import React from "react";
import { ShieldCheck } from "lucide-react";

/**
 * Outcome Guarantee badge — the central conversion-trust element.
 *
 * Wording: "Get 5 internal referrals in 30 days — or keep using ChillRefer
 * free until you do." NOT money-back — we extend free usage instead. This
 * is genuinely better for the user (they keep the tool that works) AND
 * better for us (no chargebacks, no refund disputes).
 *
 * Used in 3 places (controlled by `variant`):
 *  - `hero`   : compact pill near the landing-page CTA
 *  - `card`   : medium card next to the pricing block
 *  - `banner` : full-width banner on the billing/checkout page (max reassurance
 *               at the moment of purchase)
 */
export default function OutcomeGuarantee({ variant = "card" }) {
  if (variant === "hero") {
    return (
      <div
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full"
        style={{
          background: "rgba(34,197,94,0.08)",
          border: "1px solid rgba(34,197,94,0.3)",
        }}
        data-testid="outcome-guarantee-hero"
      >
        <ShieldCheck size={13} style={{ color: "#22c55e" }} />
        <span style={{ fontSize: "0.72rem", fontWeight: 700, color: "#22c55e", letterSpacing: "0.02em" }}>
          5 referrals in 30 days, or extra days free
        </span>
      </div>
    );
  }

  if (variant === "banner") {
    return (
      <div
        className="rounded-xl p-5 sm:p-6"
        style={{
          background: "linear-gradient(135deg, rgba(34,197,94,0.08), rgba(34,197,94,0.02))",
          border: "1px solid rgba(34,197,94,0.3)",
        }}
        data-testid="outcome-guarantee-banner"
      >
        <div className="flex items-start gap-4">
          <div
            className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center"
            style={{ background: "rgba(34,197,94,0.15)", border: "1px solid rgba(34,197,94,0.3)" }}
          >
            <ShieldCheck size={22} style={{ color: "#22c55e" }} />
          </div>
          <div className="flex-1 min-w-0">
            <p
              className="text-xs font-bold uppercase tracking-wider mb-1"
              style={{ color: "#22c55e" }}
            >
              Outcome guarantee
            </p>
            <h3 className="text-lg sm:text-xl font-black mb-1.5" style={{ color: "#fafafa", lineHeight: 1.3 }}>
              Get 5 internal referrals in 30 days &mdash; or keep using ChillRefer free until you do.
            </h3>
            <p className="text-sm" style={{ color: "rgba(255,255,255,0.55)", lineHeight: 1.55 }}>
              No fine print. No "you didn't follow the steps." If ChillRefer doesn't deliver 5 real
              referral conversations within your first month, we extend your subscription free until
              it happens. The risk is on us.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Default: "card" variant
  return (
    <div
      className="rounded-xl p-5 max-w-md mx-auto"
      style={{
        background: "rgba(34,197,94,0.05)",
        border: "1px solid rgba(34,197,94,0.25)",
      }}
      data-testid="outcome-guarantee-card"
    >
      <div className="flex items-center gap-2 mb-2">
        <ShieldCheck size={16} style={{ color: "#22c55e" }} />
        <span
          className="text-xs font-bold uppercase tracking-wider"
          style={{ color: "#22c55e" }}
        >
          Outcome guarantee
        </span>
      </div>
      <p className="text-sm font-bold mb-1" style={{ color: "#fafafa", lineHeight: 1.4 }}>
        Get 5 internal referrals in 30 days &mdash; or stay on us until you do.
      </p>
      <p className="text-xs" style={{ color: "rgba(255,255,255,0.5)", lineHeight: 1.55 }}>
        We extend your subscription free if 5 referrals don't land in your first month.
      </p>
    </div>
  );
}
